import React, { type FC, type ReactNode, useContext, useMemo, useState } from "react";
import type { MicroFrontendPayload } from "@entur-partner/micro-frontend";
import { type Permission, PermissionType } from "@entur-partner/permission-client-node";
import { assertIsDefined, hasOneOfPermissions } from "@entur-partner/util";

import { applySupportPreview, readStoredSupportPreview, storeSupportPreview } from "@app/supportPreview.ts";
import { ADMIN_ONLY_CAPABILITIES } from "@/AccessPermissions.ts";
import { $api } from "@/api/ops-api-client.ts";

type AppContextValue = MicroFrontendPayload & {
  activeOrgId: number;
  canPreview: boolean;
  previewing: boolean;
  setPreviewing: (enabled: boolean) => void;
};

const AppContext = React.createContext<AppContextValue | undefined>(undefined);

export const useUser = () => {
  const context = useContext(AppContext);
  assertIsDefined(context);
  return context.user;
};

export const useSupportPreview = () => {
  const context = useContext(AppContext);
  assertIsDefined(context, "useSupportPreview can't be used outside AppProvider");
  return {
    canPreview: context.canPreview,
    previewing: context.previewing,
    setPreviewing: context.setPreviewing
  };
};

export const useLayoutContext = () => {
  const context = useContext(AppContext);
  assertIsDefined(context, "useLayoutContext can't be used outside AppProvider");
  return {
    setMFNavigate: context.setMFNavigate,
    language: context.language,
    addMenuItems: context.addMenuItems
  };
};

export const AppProvider: FC<MicroFrontendPayload & { children: ReactNode }> = ({ children, ...rest }) => {
  const { data: permissions } = $api.useSuspenseQuery(
    "get",
    "/permissions",
    {},
    {
      // We don't use responsibility scoping; those fields stay null.
      select: (data): Permission[] =>
        data.map((p) => ({
          operation: p.operation,
          access: p.access,
          responsibilityType: null,
          responsibilityKey: null,
          type: PermissionType.BUSINESS_CAPABILITY
        }))
    }
  );

  const [previewEnabled, setPreviewEnabled] = useState(readStoredSupportPreview);

  // Append (don't override) so the host's cross-MF wrappers stay in user.permissions.
  // Memoized so consumer effects don't re-fire on every render.
  const mergedPermissions = useMemo(
    () => [...rest.user.permissions, ...permissions],
    [rest.user.permissions, permissions]
  );

  const canPreview = hasOneOfPermissions(ADMIN_ONLY_CAPABILITIES, mergedPermissions);
  const previewing = canPreview && previewEnabled;

  const effectivePermissions = useMemo(
    () => (previewing ? applySupportPreview(mergedPermissions) : mergedPermissions),
    [mergedPermissions, previewing]
  );

  const setPreviewing = (enabled: boolean) => {
    storeSupportPreview(enabled);
    setPreviewEnabled(enabled);
  };

  return (
    <AppContext.Provider
      value={{
        ...rest,
        user: { ...rest.user, permissions: effectivePermissions },
        activeOrgId: Number(rest.organisationId),
        canPreview,
        previewing,
        setPreviewing
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
